import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useUser } from './hooks/useUser';
import * as authentikOidc from './auth/authentikOidc';
import './PrivateRoute.css';

/**
 * PrivateRoute
 *
 * Gates the protected app on:
 *   1. an Authentik session (id_token present + not expired)
 *   2. an active or trialing subscription on the Postgres user record
 *
 * Subscription is now Postgres-canonical via /api/users/me, no JWT custom-claim
 * roles to inspect. Logout always goes through `authentikOidc.logout()`, which
 * clears local storage and redirects to Authentik's end-session URL.
 */
function LoadingScreen() {
  return (
    <div className="access-loading">
      <span className="access-loading__icon">🔐</span>
      <p className="access-loading__text">Even geduld...</p>
      <p className="access-loading__subtext">We controleren je sessie</p>
    </div>
  );
}

function NoUserScreen({ onLogout }) {
  return (
    <div className="access-page">
      <div className="access-card">
        <span className="access-icon">⚠️</span>
        <h1 className="access-title">Account niet gevonden</h1>
        <p className="access-description">
          We konden je account niet vinden in onze administratie. Neem contact op
          met support of probeer opnieuw in te loggen.
        </p>
        <div className="access-actions">
          <button
            type="button"
            onClick={onLogout}
            className="access-btn access-btn--secondary"
          >
            🚪 Uitloggen
          </button>
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ onRetry }) {
  return (
    <div className="access-page">
      <div className="access-card">
        <span className="access-icon">📡</span>
        <h1 className="access-title">Verbindingsprobleem</h1>
        <p className="access-description">
          Even geen verbinding met de server. Controleer je internet en probeer
          het opnieuw.
        </p>
        <div className="access-actions">
          <button
            type="button"
            onClick={onRetry}
            className="access-btn access-btn--primary"
          >
            🔄 Opnieuw proberen
          </button>
        </div>
      </div>
    </div>
  );
}

function NoSubscriptionScreen({ user, status, onLogout }) {
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const isTrialExpired = status === 'trial_expired';

  const handleAuthenticatedCheckout = async (e) => {
    e.preventDefault();
    setCheckoutLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      // /api/stripe/create-checkout-session handles both new and existing
      // customers; it returns 409 SUBSCRIPTION_EXISTS when there is already
      // an active sub, with portalUrl set so the UI can redirect to manage.
      const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          name: user.name,
          plan: 'personal',
        }),
      });

      const data = await response.json();

      if (response.status === 409 && data.code === 'SUBSCRIPTION_EXISTS') {
        toast.success(data.message, { duration: 5000 });
        if (data.portalUrl) {
          window.location.href = data.portalUrl;
        }
        setCheckoutLoading(false);
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.error || 'Kon checkout niet starten. Probeer het opnieuw.');
        setCheckoutLoading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Er ging iets mis. Probeer het opnieuw.');
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="access-page">
      <div className="access-card">
        <span className="access-icon">{isTrialExpired ? '⏰' : '🔒'}</span>
        <h1 className="access-title">
          {isTrialExpired ? 'Proefperiode Verlopen' : 'Abonnement Vereist'}
        </h1>
        <p className="access-description">
          {isTrialExpired
            ? 'Je 7-daagse gratis proefperiode is afgelopen. Start een abonnement om verder te gaan met Humanaize Matcher.'
            : 'Je hebt een actief abonnement nodig om toegang te krijgen tot Humanaize Matcher.'}
        </p>

        <div className="access-actions">
          <button
            onClick={handleAuthenticatedCheckout}
            disabled={checkoutLoading}
            className="access-btn access-btn--primary"
          >
            {checkoutLoading ? '⏳ Bezig...' : '💳 Start Abonnement (€49/maand)'}
          </button>
          <Link to="/account" className="access-btn access-btn--secondary">
            ⚙️ Account Beheren
          </Link>
          <button
            type="button"
            onClick={onLogout}
            className="access-btn access-btn--secondary"
          >
            🚪 Uitloggen
          </button>
        </div>

        <div className="access-note">
          <p>
            💡 Heb je al betaald? Refresh de pagina of neem contact op met support.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function PrivateRoute({ children }) {
  const { user, isLoading, error, refetch } = useUser();
  // Two separate refs so a single mount that traverses both paths (e.g. HMR)
  // never silently swallows one of the notifications.
  const subToastRef = useRef(false);
  const devWarnRef = useRef(false);
  // Guards the Authentik login redirect so it fires at most once per mount,
  // even under StrictMode double-invoke or unrelated re-renders.
  const loginRedirectingRef = useRef(false);

  // 🛠 LOCAL DEV ONLY, subscription bypass.
  // Triple-gated: requires NODE_ENV === 'development' (never true in prod builds)
  // AND the REACT_APP_BYPASS_SUBSCRIPTION flag from .env.development (not loaded in prod).
  const devBypass =
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_BYPASS_SUBSCRIPTION === 'true';

  // Surface a generic "no access" toast once per mount when the user is logged
  // in but lacks an active/trialing subscription. Generic phrasing, never
  // leak internal status codes to end users.
  useEffect(() => {
    if (isLoading || !user) return;
    const status = user.subscriptionStatus || 'none';
    const isSubscribed = status === 'active' || status === 'trialing';
    if (!isSubscribed && !subToastRef.current) {
      const msg = status === 'trial_expired'
        ? '⏰ Je proefperiode is verlopen. Start een abonnement om verder te gaan.'
        : '🔒 Je hebt geen actief abonnement. Start een abonnement of neem contact op met support.';
      toast.error(msg, { duration: 5000, position: 'top-center' });
      subToastRef.current = true;
    }
  }, [user, isLoading]);

  // Login redirect lives in an effect, calling authentikOidc.login() during
  // render writes PKCE state to sessionStorage and sets window.location.href,
  // which is a side-effect that breaks under StrictMode and on any incidental
  // re-render before navigation completes. The ref ensures we only fire once.
  useEffect(() => {
    if (isLoading) return;
    if (devBypass) return;
    if (authentikOidc.isAuthenticated()) return;
    if (loginRedirectingRef.current) return;
    loginRedirectingRef.current = true;
    authentikOidc.login({ returnTo: window.location.pathname });
  }, [isLoading, devBypass]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Not authenticated, the effect above is kicking off the Authentik redirect.
  // Render a loading screen while we wait for window.location to navigate.
  if (!authentikOidc.isAuthenticated()) {
    if (devBypass) {
      // Dev bypass also lets us skip the auth gate entirely
      return (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 9999,
              background: '#f59e0b',
              color: '#000',
              textAlign: 'center',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            ⚠️ DEV MODE, auth + subscription bypass actief (REACT_APP_BYPASS_SUBSCRIPTION=true)
          </div>
          {children}
        </>
      );
    }
    return <LoadingScreen />;
  }

  // Distinguish a genuine network/server failure from a missing DB row so a
  // brief Wi-Fi blip doesn't get reported to the user as "Account niet gevonden".
  if (error) {
    return <ErrorScreen onRetry={() => refetch()} />;
  }

  // Logged in, but no Postgres user record. Stripe webhook may have failed
  // to claim the entitlement. Show a generic "account not found" screen.
  if (!user) {
    return <NoUserScreen onLogout={() => authentikOidc.logout()} />;
  }

  const status = user.subscriptionStatus || 'none';
  const isSubscribed = status === 'active' || status === 'trialing';

  if (devBypass) {
    if (!devWarnRef.current) {
      console.warn(
        '⚠️ [DEV] Subscription gate BYPASSED via REACT_APP_BYPASS_SUBSCRIPTION. ' +
        'This flag is ignored in production builds, see PrivateRoute.jsx.'
      );
      devWarnRef.current = true;
    }
    return (
      <>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            background: '#f59e0b',
            color: '#000',
            textAlign: 'center',
            padding: '6px 12px',
            fontSize: '13px',
            fontWeight: 600,
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          ⚠️ DEV MODE, subscription bypass actief (REACT_APP_BYPASS_SUBSCRIPTION=true)
        </div>
        {children}
      </>
    );
  }

  if (!isSubscribed) {
    return (
      <NoSubscriptionScreen
        user={user}
        status={status}
        onLogout={() => authentikOidc.logout()}
      />
    );
  }

  return children;
}
